import { useLoadableRemoteConfig } from '@hackler/react-sdk';
import { atom } from 'jotai';
import type { MutableRefObject } from 'react';

import { useInhouseRemoteConfig } from '@/api/common/inhouse-remote-config';
import {
  type RemoteConfigKeys,
  type RemoteConfigValue,
} from '@/constants/remoteConfigKeys';

type OffsetTopList = number[];
export type Section = 'intro' | 'partner' | 'careerSupport' | 'schedule';
type SectionRefList = SectionRef[];

export const offsetTopListAtom = atom<OffsetTopList>([0, 1, 2, 3]);
export const activeSectionAtom = atom<Section>('intro');
export type SectionRef = MutableRefObject<any>;

const mobileNavHeight = 36;

const getOffsetTop = (ref: SectionRef): number => {
  return ref && ref.current ? ref.current.offsetTop - mobileNavHeight : 0;
};
const getOffsetTopList = (refs: SectionRefList): number[] => {
  return refs.map((ref) => getOffsetTop(ref));
};

export const updateOffsetTopListAtom = atom(
  (get) => get(offsetTopListAtom),
  (get, set, sectionRefList: SectionRefList) => {
    set(offsetTopListAtom, getOffsetTopList(sectionRefList.sort()));
  },
);

export const useCustomRemoteConfig = <T = any>(key: string): T | null => {
  const { isLoading, remoteConfig } = useLoadableRemoteConfig();
  const parameterValue = remoteConfig.get(key, 'defaultValue');
  const isRemoteConfigOn = !isLoading && parameterValue !== 'defaultValue';
  return isRemoteConfigOn ? JSON.parse(parameterValue) : null;
};

// 타입에 따른 Parser 함수를 받아서 처리
export const useCustomInhouseRemoteConfig = <T = any>(
  key: string,
  type: 'boolean' | 'json' | 'string' = 'json',
  enabled: boolean = true,
): T | null => {
  // type에 따른 parser 자동 선택
  const parser = getParserByType(type);

  const { value, isLoading, error } = useInhouseRemoteConfig(
    key,
    parser,
    enabled,
  );

  if (isLoading || error) {
    return null;
  }

  return value;
};

/**
 * 타입 안전한 원격구성 훅
 * RemoteConfigKeys enum을 사용하여 타입 안전성을 보장합니다.
 *
 * @param key - RemoteConfigKeys enum 값
 * @param type - 반환될 값의 타입 (기본값: 'json')
 * @param enabled - 훅 활성화 여부 (기본값: true)
 * @returns 타입이 보장된 원격구성 값 또는 null
 *
 * @example
 * const gnbEduMenu = useTypedInhouseRemoteConfig(RemoteConfigKeys.GNB_EDU_MENU, 'boolean');
 */
export const useTypedInhouseRemoteConfig = <T extends RemoteConfigKeys>(
  key: T,
  type: 'boolean' | 'json' | 'string' = 'json',
  enabled: boolean = true,
): RemoteConfigValue<T> | null => {
  return useCustomInhouseRemoteConfig<RemoteConfigValue<T>>(key, type, enabled);
};

// type별 parser 함수들
const getParserByType = (type: 'boolean' | 'json' | 'string') => {
  switch (type) {
    case 'boolean':
      return (value: string) => value === 'true';
    case 'json':
      return (value: string) => JSON.parse(value);
    case 'string':
      return (value: string) => value;
    default:
      return (value: string) => value;
  }
};
